/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useContactsWASP - Hook React para API de Contactos WASP v2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file hooks/useContactsWASP.ts
 * @version 1.0.0
 * @description Hook para gestión de contactos con schema v2.0 (Digital Twin)
 * 
 * CARACTERÍSTICAS:
 * - CRUD completo con validaciones
 * - Import masivo CSV/JSON
 * - Búsqueda y filtros avanzados
 * - Cache inteligente
 * - Manejo de errores robusto
 * - TypeScript completo
 * - Optimistic updates
 * 
 * REEMPLAZA: useContactsDatabase (mock)
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import axios, { AxiosError } from 'axios';
import { getHatateToken } from '../helpers/utils';

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS (importar desde ContactWASP_schema.ts)
// ═══════════════════════════════════════════════════════════════════════════

export interface ContactWASP {
  // CORE
  id: string;
  phone: string;
  
  // DATOS PERSONALES
  name: string;
  apellido?: string | null;
  email?: string | null;
  rut?: string | null;
  direccion?: string | null;
  
  // DATOS PROFESIONALES
  company?: string | null;
  position?: string | null;
  
  // ORGANIZACIÓN INTERNA
  tags: string[];
  groups: string[];
  status: 'active' | 'inactive' | 'blocked' | 'pending';
  notes?: string | null;
  
  // LINKHUB
  linkhub_enabled?: boolean;
  linkhub_password_hash?: string | null;
  linkhub_verified?: boolean;
  linkhub_last_login?: string | null;
  linkhub_permissions?: string[];
  
  // AI CONTEXT
  ai_context?: AIContext | null;
  
  // METADATA SISTEMA
  source: 'manual' | 'csv' | 'ai' | 'import' | 'conversation' | 'api';
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  
  // GOD MODE
  internal_notes?: string | null;
  custom_fields?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface AIContext {
  interests?: {
    hobbies?: string[];
    topics?: string[];
    brands?: string[];
    detected_at?: string;
    confidence?: number;
  };
  personality?: {
    traits?: string[];
    communication_style?: 'formal' | 'casual' | 'técnico' | 'amigable';
    preferred_language?: string;
    tone?: 'profesional' | 'amigable' | 'directo' | 'empático';
    humor_level?: number;
    detected_at?: string;
    confidence?: number;
  };
  health?: {
    conditions?: string[];
    allergies?: string[];
    medications?: string[];
    lifestyle?: string[];
    detected_at?: string;
    privacy_level?: 'public' | 'private' | 'confidential' | 'restricted';
    consent_given?: boolean;
    consent_date?: string;
  };
  [key: string]: any;
}

export interface ContactFilters {
  status?: 'active' | 'inactive' | 'blocked' | 'pending';
  tags?: string[];
  groups?: string[];
  search?: string;
  page?: number;
  limit?: number;
}

export interface ContactStats {
  total: number;
  byStatus: {
    active: number;
    inactive: number;
    blocked: number;
    pending: number;
  };
  bySource: Record<string, number>;
  withEmail: number;
  withRUT: number;
  withDireccion: number;
  linkhubEnabled: number;
}

export interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  total: number;
  errors: Array<{
    index: number;
    phone: string;
    errors: string[];
  }>;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE = 'https://backend.anubisai.net/api/whatsapp/wasp';

// Cache TTL: 2 minutos
const CACHE_TTL = 2 * 60 * 1000;

// ═══════════════════════════════════════════════════════════════════════════
// TIPOS INTERNOS
// ═══════════════════════════════════════════════════════════════════════════

interface CacheEntry {
  data: ContactWASP[];
  timestamp: number;
  filters?: ContactFilters;
}

interface UseContactsWASPReturn {
  // Estado
  contacts: ContactWASP[];
  isLoading: boolean;
  error: string | null;
  total: number;
  page: number;
  totalPages: number;
  
  // CRUD
  addContact: (data: Partial<ContactWASP>) => Promise<ContactWASP | null>;
  updateContact: (id: string, data: Partial<ContactWASP>) => Promise<ContactWASP | null>;
  deleteContact: (id: string) => Promise<boolean>;
  getContactById: (id: string) => Promise<ContactWASP | null>;
  getContactByPhone: (phone: string) => Promise<ContactWASP | null>;
  
  // Búsqueda y filtros
  searchContacts: (filters: ContactFilters) => Promise<void>;
  refresh: () => Promise<void>;
  
  // Import
  importContacts: (contacts: Partial<ContactWASP>[]) => Promise<ImportResult | null>;
  
  // Stats
  getStats: () => Promise<ContactStats | null>;
  
  // Utils
  clearError: () => void;
  clearCache: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

export const useContactsWASP = (
  userId: string,
  initialFilters?: ContactFilters
): UseContactsWASPReturn => {
  
  // ═══════════════════════════════════════════════════════════════════════
  // ESTADO
  // ═══════════════════════════════════════════════════════════════════════
  
  const [contacts, setContacts] = useState<ContactWASP[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [cache, setCache] = useState<CacheEntry | null>(null);
  
  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════
  
  /**
   * Obtiene headers para requests
   */
  const getHeaders = useCallback(() => {
    const token = getHatateToken();
    
    return {
      'Authorization': token,
      'X-API-Key': 'anubis-whatsapp-api-key',
      'X-User-ID': userId,
      'Content-Type': 'application/json'
    };
  }, [userId]); // ← Agregar userId a dependencies
  
  /**
   * Manejo de errores centralizado
   */
  const handleError = useCallback((err: any, context: string) => {
    console.error(`[useContactsWASP] ${context}:`, err);
    
    let errorMessage = 'Error desconocido';
    
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError<any>;
      
      if (axiosError.response) {
        errorMessage = axiosError.response.data?.error || 
                      axiosError.response.data?.message ||
                      `Error ${axiosError.response.status}`;
      } else if (axiosError.request) {
        errorMessage = 'No se pudo conectar con el servidor';
      } else {
        errorMessage = axiosError.message;
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
    }
    
    setError(errorMessage);
    return errorMessage;
  }, []);
  
  /**
   * Verifica si el cache es válido
   */
  const isCacheValid = useCallback((filters?: ContactFilters): boolean => {
    if (!cache) return false;
    
    const now = Date.now();
    const isExpired = (now - cache.timestamp) > CACHE_TTL;
    
    if (isExpired) return false;
    
    // Verificar si los filtros coinciden
    if (filters && cache.filters) {
      return JSON.stringify(filters) === JSON.stringify(cache.filters);
    }
    
    return !filters && !cache.filters;
  }, [cache]);
  
  // ═══════════════════════════════════════════════════════════════════════
  // CRUD OPERATIONS
  // ═══════════════════════════════════════════════════════════════════════
  
  /**
   * Obtiene lista de contactos con filtros
   */
  const fetchContacts = useCallback(async (filters?: ContactFilters) => {
    // Verificar cache
    if (isCacheValid(filters)) {
      console.log('[useContactsWASP] Using cached contacts');
      setContacts(cache!.data);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const params: any = {};
      
      if (filters?.status) params.status = filters.status;
      if (filters?.tags) params.tags = filters.tags;
      if (filters?.groups) params.groups = filters.groups;
      if (filters?.search) params.search = filters.search;
      if (filters?.page) params.page = filters.page;
      if (filters?.limit) params.limit = filters.limit;
      
      const response = await axios.get(`${API_BASE}/contacts`, {
        headers: getHeaders(),
        params,
        timeout: 30000
      });
      
      if (response.data.success) {
        const fetchedContacts = response.data.contacts || [];
        
        setContacts(fetchedContacts);
        setTotal(response.data.total || 0);
        setPage(response.data.page || 1);
        setTotalPages(response.data.totalPages || 1);
        
        // Actualizar cache
        setCache({
          data: fetchedContacts,
          timestamp: Date.now(),
          filters
        });
        
        console.log('[useContactsWASP] Contacts fetched:', {
          count: fetchedContacts.length,
          total: response.data.total
        });
      } else {
        throw new Error(response.data.error || 'Failed to fetch contacts');
      }
      
    } catch (err) {
      handleError(err, 'fetchContacts');
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  }, [getHeaders, handleError, isCacheValid, cache]);
  
  /**
   * Crea un nuevo contacto
   */
  const addContact = useCallback(async (
    data: Partial<ContactWASP>
  ): Promise<ContactWASP | null> => {
    setError(null);
    
    try {
      const response = await axios.post(
        `${API_BASE}/contacts`,
        data,
        {
          headers: getHeaders(),
          timeout: 30000
        }
      );
      
      if (response.data.success) {
        const newContact = response.data.contact;
        
        // Optimistic update
        setContacts(prev => [newContact, ...prev]);
        setTotal(prev => prev + 1);
        
        // Invalidar cache
        setCache(null);
        
        console.log('[useContactsWASP] Contact created:', newContact.id);
        return newContact;
      } else {
        throw new Error(response.data.error || 'Failed to create contact');
      }
      
    } catch (err) {
      handleError(err, 'addContact');
      return null;
    }
  }, [getHeaders, handleError]);
  
  /**
   * Actualiza un contacto existente
   */
  const updateContact = useCallback(async (
    id: string,
    data: Partial<ContactWASP>
  ): Promise<ContactWASP | null> => {
    setError(null);
    
    try {
      const response = await axios.put(
        `${API_BASE}/contacts/${id}`,
        data,
        {
          headers: getHeaders(),
          timeout: 30000
        }
      );
      
      if (response.data.success) {
        const updatedContact = response.data.contact;
        
        // Optimistic update
        setContacts(prev => 
          prev.map(c => c.id === id ? updatedContact : c)
        );
        
        // Invalidar cache
        setCache(null);
        
        console.log('[useContactsWASP] Contact updated:', id);
        return updatedContact;
      } else {
        throw new Error(response.data.error || 'Failed to update contact');
      }
      
    } catch (err) {
      handleError(err, 'updateContact');
      return null;
    }
  }, [getHeaders, handleError]);
  
  /**
   * Elimina un contacto
   */
  const deleteContact = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    
    try {
      const response = await axios.delete(
        `${API_BASE}/contacts/${id}`,
        {
          headers: getHeaders(),
          timeout: 30000
        }
      );
      
      if (response.data.success) {
        // Optimistic update
        setContacts(prev => prev.filter(c => c.id !== id));
        setTotal(prev => Math.max(0, prev - 1));
        
        // Invalidar cache
        setCache(null);
        
        console.log('[useContactsWASP] Contact deleted:', id);
        return true;
      } else {
        throw new Error(response.data.error || 'Failed to delete contact');
      }
      
    } catch (err) {
      handleError(err, 'deleteContact');
      return false;
    }
  }, [getHeaders, handleError]);
  
  /**
   * Obtiene un contacto por ID
   */
  const getContactById = useCallback(async (
    id: string
  ): Promise<ContactWASP | null> => {
    setError(null);
    
    try {
      // Verificar si está en cache local
      const cachedContact = contacts.find(c => c.id === id);
      if (cachedContact) {
        console.log('[useContactsWASP] Contact found in cache:', id);
        return cachedContact;
      }
      
      // Fetch desde API
      const response = await axios.get(
        `${API_BASE}/contacts/${id}`,
        {
          headers: getHeaders(),
          timeout: 30000
        }
      );
      
      if (response.data.success) {
        console.log('[useContactsWASP] Contact fetched by ID:', id);
        return response.data.contact;
      } else {
        throw new Error(response.data.error || 'Contact not found');
      }
      
    } catch (err) {
      handleError(err, 'getContactById');
      return null;
    }
  }, [contacts, getHeaders, handleError]);
  
  /**
   * Obtiene un contacto por teléfono
   */
  const getContactByPhone = useCallback(async (
    phone: string
  ): Promise<ContactWASP | null> => {
    setError(null);
    
    try {
      // Verificar si está en cache local
      const cachedContact = contacts.find(c => c.phone === phone);
      if (cachedContact) {
        console.log('[useContactsWASP] Contact found in cache by phone:', phone);
        return cachedContact;
      }
      
      // Fetch desde API
      const response = await axios.get(
        `${API_BASE}/contacts/phone/${encodeURIComponent(phone)}`,
        {
          headers: getHeaders(),
          timeout: 30000
        }
      );
      
      if (response.data.success) {
        console.log('[useContactsWASP] Contact fetched by phone:', phone);
        return response.data.contact;
      } else {
        throw new Error(response.data.error || 'Contact not found');
      }
      
    } catch (err) {
      handleError(err, 'getContactByPhone');
      return null;
    }
  }, [contacts, getHeaders, handleError]);
  
  // ═══════════════════════════════════════════════════════════════════════
  // BÚSQUEDA Y FILTROS
  // ═══════════════════════════════════════════════════════════════════════
  
  /**
   * Busca contactos con filtros
   */
  const searchContacts = useCallback(async (filters: ContactFilters) => {
    await fetchContacts(filters);
  }, [fetchContacts]);
  
  /**
   * Refresca la lista de contactos
   */
  const refresh = useCallback(async () => {
    setCache(null); // Invalidar cache
    await fetchContacts(initialFilters);
  }, [fetchContacts, initialFilters]);
  
  // ═══════════════════════════════════════════════════════════════════════
  // IMPORT
  // ═══════════════════════════════════════════════════════════════════════
  
  /**
   * Importa múltiples contactos
   */
  const importContacts = useCallback(async (
    contactsData: Partial<ContactWASP>[]
  ): Promise<ImportResult | null> => {
    setError(null);
    
    try {
      const response = await axios.post(
        `${API_BASE}/contacts/import`,
        { contacts: contactsData },
        {
          headers: getHeaders(),
          timeout: 60000 // 60 segundos para imports grandes
        }
      );
      
      if (response.data.success) {
        const result: ImportResult = {
          success: true,
          imported: response.data.imported,
          skipped: response.data.skipped,
          total: response.data.total,
          errors: response.data.errors || []
        };
        
        // Invalidar cache y refrescar
        setCache(null);
        await fetchContacts(initialFilters);
        
        console.log('[useContactsWASP] Import completed:', result);
        return result;
      } else {
        throw new Error(response.data.error || 'Failed to import contacts');
      }
      
    } catch (err) {
      handleError(err, 'importContacts');
      return null;
    }
  }, [getHeaders, handleError, fetchContacts, initialFilters]);
  
  // ═══════════════════════════════════════════════════════════════════════
  // STATS
  // ═══════════════════════════════════════════════════════════════════════
  
  /**
   * Obtiene estadísticas de contactos
   */
  const getStats = useCallback(async (): Promise<ContactStats | null> => {
    setError(null);
    
    try {
      const response = await axios.get(
        `${API_BASE}/contacts/stats`,
        {
          headers: getHeaders(),
          timeout: 30000
        }
      );
      
      if (response.data.success) {
        console.log('[useContactsWASP] Stats fetched');
        return response.data.stats;
      } else {
        throw new Error(response.data.error || 'Failed to get stats');
      }
      
    } catch (err) {
      handleError(err, 'getStats');
      return null;
    }
  }, [getHeaders, handleError]);
  
  // ═══════════════════════════════════════════════════════════════════════
  // UTILS
  // ═══════════════════════════════════════════════════════════════════════
  
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  const clearCache = useCallback(() => {
    setCache(null);
    console.log('[useContactsWASP] Cache cleared');
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════
  
  /**
   * Fetch inicial
   */
  useEffect(() => {
    fetchContacts(initialFilters);
  }, [fetchContacts, initialFilters]);
  
  // ═══════════════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════════════
  
  return {
    // Estado
    contacts,
    isLoading,
    error,
    total,
    page,
    totalPages,
    
    // CRUD
    addContact,
    updateContact,
    deleteContact,
    getContactById,
    getContactByPhone,
    
    // Búsqueda
    searchContacts,
    refresh,
    
    // Import
    importContacts,
    
    // Stats
    getStats,
    
    // Utils
    clearError,
    clearCache
  };
};

export default useContactsWASP;